import {skipToken} from '@reduxjs/toolkit/query';
import {compareDesc} from 'date-fns';

import {useRef, useState} from 'react';

import {equals, isNil, pick} from 'ramda';

import {parseDate, useLatestValue, useOnMount} from 'shared/web';

import type {
  BulkOperationResponseBody,
  DataGridBulkActionsResponseBody,
} from '../types/api/bulkOperations';
import type {BulkOperationsServices} from '../types/BulkOperationsServices';
import {createBulkOperationRefresh} from '../utils/createBulkOperationRefresh';
import {isBulkOperationSupportedByGrid} from '../utils/isBulkOperationSupportedByGrid';
import {isBulkOperationTerminal} from '../utils/isBulkOperationTerminal';
import {reconcileBulkOperation, type BulkOperationRecord} from '../utils/reconcileBulkOperation';

/** LIST page size for the tracked-operations panel; the API caps limit at 100. */
export const BULK_OPERATION_PANEL_LIMIT = 20;

const PROGRESS_FIELDS = [
  'bulkOperationId',
  'resourceId',
  'actionCode',
  'state',
  'totalCount',
  'doneCount',
  'failedCount',
  'skippedCount',
] as const;

type TrackingState = {
  operations: Map<string, BulkOperationRecord>;
  /** Terminal progress does not guarantee that final error/finish metadata was fetched. */
  pendingFinalDetail: Set<string>;
  inputs: {
    restored?: BulkOperationResponseBody[];
    initial?: BulkOperationResponseBody;
    detail?: BulkOperationResponseBody;
  };
  /** Ids the current list snapshot vouches for; cleared while a replacement list is in flight. */
  restoredIds: string[];
  /** Ids this mount submitted or adopted from pushes; they outlive list refetches. */
  sessionIds: Set<string>;
};

function withSessionId(state: TrackingState, bulkOperationId: string) {
  return state.sessionIds.has(bulkOperationId)
    ? state
    : {...state, sessionIds: new Set(state.sessionIds).add(bulkOperationId)};
}

function acceptOperation(state: TrackingState, incoming: BulkOperationRecord) {
  const id = incoming.value.bulkOperationId;
  const current = state.operations.get(id);
  const operation = reconcileBulkOperation(current, incoming);
  const pendingFinalDetail = new Set(state.pendingFinalDetail);
  if (isBulkOperationTerminal(incoming.value.state)) {
    if (incoming.type === 'progress') {
      pendingFinalDetail.add(id);
    } else {
      pendingFinalDetail.delete(id);
    }
  }
  return operation === current && equals(pendingFinalDetail, state.pendingFinalDetail)
    ? state
    : {
        ...state,
        pendingFinalDetail,
        operations: new Map(state.operations).set(operation.value.bulkOperationId, operation),
      };
}

function getOperation(state: TrackingState, id: string | undefined) {
  const operation = isNil(id) ? undefined : state.operations.get(id);
  return operation?.type === 'detail' ? operation.value : undefined;
}

/** Records needing a server refresh: still running, or push-only snapshots awaiting detail. */
function needsRefresh(record: BulkOperationRecord) {
  return record.type === 'progress' || !isBulkOperationTerminal(record.value.state);
}

/** One acceptance policy and refresh owner for every operation the grid footer tracks. */
export function useBulkOperationTracking(args: {
  services: BulkOperationsServices;
  discovery?: DataGridBulkActionsResponseBody;
  linkedOperationId?: string;
  initialOperation?: BulkOperationResponseBody;
}) {
  const [state, setState] = useState<TrackingState>(() => ({
    operations: new Map(),
    pendingFinalDetail: new Set(),
    inputs: {},
    restoredIds: [],
    sessionIds: new Set(),
  }));
  const discovery = args.discovery;
  const {
    currentData: latest = [],
    isFetching,
    isError,
  } = args.services.useListOperations(
    isNil(discovery)
      ? skipToken
      : {
          resourceId: discovery.resourceId,
          actionCode: discovery.actions.map((action) => action.actionCode),
          limit: BULK_OPERATION_PANEL_LIMIT,
        },
    {
      skip: discovery?.actions.length === 0,
      refetchOnMountOrArgChange: true,
    }
  );
  const restored =
    !isFetching && !isError && !isNil(discovery)
      ? latest.filter((candidate) => isBulkOperationSupportedByGrid(candidate, discovery))
      : undefined;
  const initial =
    args.initialOperation?.bulkOperationId === args.linkedOperationId
      ? args.initialOperation
      : undefined;
  const knownLinked =
    !isNil(args.linkedOperationId) &&
    (!isNil(getOperation(state, args.linkedOperationId)) ||
      restored?.some((candidate) => candidate.bulkOperationId === args.linkedOperationId) ===
        true ||
      !isNil(initial));
  const detail = args.services.useGetOperation(
    isNil(args.linkedOperationId) ? skipToken : {bulkOperationId: args.linkedOperationId},
    {
      // Standalone results have no fresh list to reconcile cached or synthetic retry snapshots.
      skip: !isNil(discovery) && knownLinked,
      refetchOnMountOrArgChange: isNil(discovery),
    }
  );

  // Consume each query snapshot once; rerenders must not replay cached data over a push.
  const inputs = {
    restored,
    initial,
    detail:
      detail.currentData?.bulkOperationId === args.linkedOperationId
        ? detail.currentData
        : undefined,
  };
  let accepted = state;
  if (!equals(state.inputs, inputs)) {
    accepted = {
      ...state,
      inputs,
      restoredIds: (inputs.restored ?? []).map((operation) => operation.bulkOperationId),
    };
    const previousRestored = new Map(
      (state.inputs.restored ?? []).map((operation) => [operation.bulkOperationId, operation])
    );
    for (const operation of inputs.restored ?? []) {
      if (!equals(previousRestored.get(operation.bulkOperationId), operation)) {
        accepted = acceptOperation(accepted, {type: 'detail', value: operation});
      }
    }
    for (const key of ['initial', 'detail'] as const) {
      const operation = inputs[key];
      if (!isNil(operation) && !equals(state.inputs[key], operation)) {
        accepted = acceptOperation(accepted, {type: 'detail', value: operation});
      }
    }
    setState(accepted);
  }
  const operations = [...new Set([...accepted.restoredIds, ...accepted.sessionIds])]
    .map((id) => getOperation(accepted, id))
    .filter((operation) => !isNil(operation))
    .toSorted((a, b) => compareDesc(parseDate(a.createdAt), parseDate(b.createdAt)));
  const linkedOperation = getOperation(accepted, args.linkedOperationId);
  const trackedIds = useLatestValue(
    new Set([...accepted.operations.keys(), args.linkedOperationId].filter((id) => !isNil(id)))
  );
  const staleIds = useLatestValue(
    new Set(
      [
        ...[...accepted.operations.entries()]
          .filter(([id, record]) => accepted.pendingFinalDetail.has(id) || needsRefresh(record))
          .map(([id]) => id),
        args.linkedOperationId,
      ].filter((id) => !isNil(id))
    )
  );
  const discoveryRef = useLatestValue(args.discovery);
  const adoptingIds = useRef(new Set<string>());
  const [loadOperation] = args.services.useLazyGetOperation();
  const getOperationRef = useLatestValue(loadOperation);
  const [refreshOwner] = useState(() =>
    createBulkOperationRefresh({
      load: (bulkOperationId) => getOperationRef.current({bulkOperationId}).unwrap(),
      isActive: (bulkOperationId) =>
        trackedIds.current.has(bulkOperationId) || adoptingIds.current.has(bulkOperationId),
      accept: (operation) => {
        adoptingIds.current.delete(operation.bulkOperationId);
        setState((current) => acceptOperation(current, {type: 'detail', value: operation}));
      },
    })
  );
  const cleanup = useRef<() => void>(() => undefined);
  const refreshActive = () =>
    staleIds.current.forEach((id) => {
      void refreshOwner.refresh(id);
    });
  args.services.useReconnectNotifications(refreshActive);
  useOnMount(
    () => {
      refreshOwner.start();
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          refreshActive();
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      cleanup.current = () => {
        refreshOwner.stop();
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    },
    () => cleanup.current()
  );

  args.services.useProgressNotifications((progress) => {
    const discovery = discoveryRef.current;
    const isTracked = trackedIds.current.has(progress.bulkOperationId);
    if (!isTracked && (isNil(discovery) || !isBulkOperationSupportedByGrid(progress, discovery))) {
      return;
    }
    if (!isTracked) {
      adoptingIds.current.add(progress.bulkOperationId);
    }
    setState((current) =>
      withSessionId(
        acceptOperation(current, {type: 'progress', value: pick(PROGRESS_FIELDS, progress)}),
        progress.bulkOperationId
      )
    );
    // A push-only record has no createdAt; adopt it with detail. Terminal pushes hydrate too.
    if (!isTracked || isBulkOperationTerminal(progress.state)) {
      void refreshOwner.refresh(progress.bulkOperationId, true);
    }
  });

  return {
    operations,
    linked: {
      operation: linkedOperation,
      isLoading: isNil(linkedOperation) && detail.isLoading,
      isError: isNil(linkedOperation) && detail.isError,
    },
    register: (operation: BulkOperationResponseBody) => {
      setState((current) =>
        withSessionId(
          acceptOperation(current, {type: 'detail', value: operation}),
          operation.bulkOperationId
        )
      );
    },
  };
}
